import styles from "./SpiderScheduling.module.css";
import { useCallback, useEffect, useState, useRef } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import AGTable from "../../components/AGTable/AGTable";
// import { Alert } from "react-bootstrap";
import TimedAlert from "../../components/TimedAlert/TimedAlert";
import { Form } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import SpiderScheduleForm from "./SpiderScheduleForm/SpiderScheduleForm";
import { toTitleCase, dayMap } from "../../utilities/functions";
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import Select from 'react-select';
import { deletePortalSchedule, getExistingSpiderSchedules, getPortalSpiderScheduleLogs, getPortalSpiderSchedules, updatePortalSchedule, kickoffSpiderSchedules } from "../../api/portalApi";

const SpiderScheduling = (props) => {
    const location = useLocation();

    // Data States
    const gridRef = useRef();
    const [spiderLibraryData, setSpiderLibraryData] = useState();
    const [portalScheduleData, setPortalScheduleData] = useState();
    const [logs, setLogs] = useState();
    const [error, setError] = useState(false);
    const [spiderFilter, setSpiderFilter] = useState('');
    const [selected, setSelected] = useState();
    const [selectedPortalSchedule, setSelectedPortalSchedule] = useState();
    const [source, setSource] = useState("portal");
    const [scheduleObj, setScheduleObj] = useState();
    const [selectedClients, setSelectedClients] = useState([]);

    // UI States
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState();
    const [showAlert, setShowAlert] = useState(false);
    const [showKickoffModal, setShowKickoffModal] = useState(false);
    const [showDeleteSchedule, setShowDeleteSchedule] = useState(false);
    const [showEditSchedule, setShowEditSchedule] = useState(false);
    const [showKickoffPortalSchedule, setShowKickoffPortalSchedule] = useState(false);

    // Fetches Zyte and Alteryx schedule data
    const fetchData = useCallback( async() => {
        setLoading(true);
        try {
            const res = await getExistingSpiderSchedules();
            setSpiderLibraryData(res?.data?.data || res?.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
        
    }, []);


    // Fetches schedules created within the portal
    const fetchPortalSchedules = useCallback( async() => {
        setLoading(true);
        try {
            const res = await getPortalSpiderSchedules();
            const data = res?.data?.data || res?.data
            setPortalScheduleData(data?.map(obj => ({
                ...obj,
                spider_args_raw: JSON.stringify(obj?.spider_args_raw)
            })));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
        
    }, []);

    // Fetches schedules created within the portal
    const fetchLogs = useCallback( async() => {
        setLoading(true);
        try {
            const res = await getPortalSpiderScheduleLogs();
            setLogs(flattenData(res?.data?.data || res?.data, []));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
        
    }, []);


    // Kicks off jobs
    const kickoffSelectedJobs = async(selectedJobs) => {
        setLoading(true);
        try {
            await kickoffSpiderSchedules(selectedJobs);
            setError();
            setAlert("Job Kicked off Successfully");
        } catch (e) {
            console.error(e);
            setError(e);
            setAlert("Error in kicking off job");
        } finally {
            setShowAlert(true);
            setLoading(false);
        }

    };


    // Deletes a scheduled job from the portal
    const deleteSchedule = async(schedule) => {
        setLoading(true);
        try {
            await deletePortalSchedule(schedule?.schedule_id);
            setError();
            fetchPortalSchedules();
            setAlert("Job Deleted Successfully");
        } catch (e) {
            console.error(e);
            setError(e);
            setAlert("Error in deleting schedule");
        } finally {
            setShowAlert(true);
            setLoading(false);
        }

    };

    const updateSchedule = async() => {
        setLoading(true);
        try {
            const postData = scheduleObj?.spiderArgs.map((args) => (
                scheduleObj?.batch_size ? 
                {
                    spider_key: selectedPortalSchedule?.spider_key,
                    spider_name: selectedPortalSchedule?.spider_name,
                    client: scheduleObj?.client,
                    spider_args_raw: {...args},
                    time: `${scheduleObj?.hour.split(":")[0]}:${scheduleObj?.minute}`,
                    day_of_week: scheduleObj?.dayOfWeek.map((day) => day.value === "Every day" ? null : day?.value.slice(0,3)).join(","),
                    day: scheduleObj?.day.map((day) => day.value === "Every day" ? null : day?.value).join(","),
                    batch_size: scheduleObj?.batch_size,
                    batch_time: scheduleObj?.batch_time,
                    schedule_id: selectedPortalSchedule?.schedule_id,
                    tag: scheduleObj?.tag,
                    arg_table: scheduleObj?.arg_table
                } :
                {
                    spider_key: selectedPortalSchedule?.spider_key,
                    spider_name: selectedPortalSchedule?.spider_name,
                    client: scheduleObj?.client,
                    spider_args_raw: {...args},
                    time: `${scheduleObj?.hour.split(":")[0]}:${scheduleObj?.minute}`,
                    day_of_week: scheduleObj?.dayOfWeek.map((day) => day.value === "Every day" ? null : day?.value.slice(0,3)).join(","),
                    day: scheduleObj?.day.map((day) => day.value === "Every day" ? null : day?.value).join(","),
                    schedule_id: selectedPortalSchedule?.schedule_id,
                    tag: scheduleObj?.tag,
                    arg_table: scheduleObj?.arg_table
                }
            ));
            await updatePortalSchedule(postData);
            setAlert("Job Updated Successfully");
            setError();
            fetchPortalSchedules();
        } catch (e) {
            console.error(e);
            setError(e);
            setAlert("Error in updating schedule");
        } finally {
            setShowAlert(true);
            setLoading(false);
        }

    };
    
    useEffect(() => { 
        if (source === "existing"){
            fetchData();
        } else if (source === "portal"){
            fetchPortalSchedules();
        } else if (source === "logs"){
            fetchLogs();
        }
    }, [source, fetchData, fetchPortalSchedules, fetchLogs]);


    // 1. Logic to sync external state to AG Grid
    const updateFilter = useCallback((api) => {
        if (!api) return;

        // // We get the current model to avoid wiping out other active filters
        // const currentModel = api.getFilterModel();
        api.getFilterModel();

        // Prepare the update for the 'spider_name' column
        const spiderModel = (spiderFilter && spiderFilter.length > 0) 
            ? { filterType: 'text', type: 'contains', filter: spiderFilter }
            : null;
            
        const clientModel = (selectedClients?.length > 0)
        ? {
            filterType: 'text', // change to 'set' if using Enterprise
            operator: 'OR',
            conditions: selectedClients.map(c => ({
                filterType: 'text', type: 'equals', filter: c.value
            }))
          }
        : null;

        // Update the model
        api.setColumnFilterModel('spider_name', spiderModel);
        api.setColumnFilterModel('client', clientModel);

        
        // Tell the grid to execute the filter logic
        api.onFilterChanged();
    }, [spiderFilter, selectedClients]);

    // 2. Effect to trigger the update when the input or grid state changes
    useEffect(() => {
        const api = gridRef.current?.api;
        
        if (api && portalScheduleData) {
            updateFilter(api);
        }
    }, [spiderFilter, updateFilter, portalScheduleData]);

    const flattenData = (data, customOrder=[]) => {
        // Function to reorder object fields
        const reorderObject = (obj) => {
            const ordered = customOrder.reduce((acc, key) => {
            if (key in obj) {
                acc[key] = obj[key];
            }
            return acc;
        }, {});
        
            Object.keys(obj).forEach((key) => {
                if (!customOrder.includes(key)) {
                    ordered[key] = obj[key];
                }
            });
            
            return ordered;
        };
        return data.map(obj =>
            Object.fromEntries(
                Object.entries(obj).map(([key, value]) => [
                    key,
                    Array.isArray(value) ? value.join(", ") : value
                ])
            )
        ).map(reorderObject);
    }

    const onGridReady = (params) => {
        updateFilter(params.api);
    }

    // Updates selection in Table
    const onSelectionChanged = (event) => {
        setSelected(event.selectedNodes.map(node => node?.data));
    }

    // Row ID for the Zyte and Alteryx table
    const getRowId = useCallback((params) => `${params?.data?.schedule_id}-${params?.data?.spider_key}-${params?.data?.spider_args}`, []);

    // Schedule Object for Updating Schedule
    useEffect(() => {
        const time = selectedPortalSchedule?.next_run_time.split("T")[1];
        setScheduleObj({
            schedule_id: selectedPortalSchedule?.schedule_id,
            client: selectedPortalSchedule?.client,
            dayOfWeek: selectedPortalSchedule?.day_of_week ? selectedPortalSchedule?.day_of_week.split(",").map((day) => ({
                value: dayMap[toTitleCase(day)],
                label: dayMap[toTitleCase(day)]
            })) : [{
                value: "Every day",
                label: "Every day"
            }],
            day: selectedPortalSchedule?.day ? selectedPortalSchedule?.day.split(",").map((day) => ({
                value: parseInt(day),
                label: day
            })) : [{
                value: "Every day",
                label: "Every day"
            }],
            hour: `${time?.substring(0,2)}:00`,
            minute: time?.substring(3,5),
            spiderArgs: selectedPortalSchedule?.spider_args_raw && JSON.parse(selectedPortalSchedule?.spider_args_raw),
            tag: selectedPortalSchedule?.tag ? selectedPortalSchedule?.tag : null,
            arg_table: selectedPortalSchedule?.arg_table ? selectedPortalSchedule?.arg_table : null
        });
    }, [selectedPortalSchedule]);

    return (
        <div>
            {alert && 
                <TimedAlert
                    message={alert}
                    duration={2000}
                    variant={error ? "danger" : "success"}
                    show={showAlert}
                    onClose={() => setShowAlert(false)}

              />
            }

            {/* Kickoff Job Modal */}
            <Modal show={showKickoffModal}>
                <Modal.Header closeButton onClick={() => setShowKickoffModal(false)}>
                    <Modal.Title>Jobs to kick off</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className={styles.SpiderSchedulingModalBody}>
                        {[...new Set(selected?.map(item => item.spider_name))].map((spider) => {
                            const jobs = selected.filter((job) => job.spider_name === spider)
                            return <span>{jobs?.length} job{jobs?.length > 1 ? "s" : ""} for {spider}</span>
                        })}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowKickoffModal(false)}>
                        Close
                    </Button>
                    <Button variant="success" onClick={() => 
                        {
                            kickoffSelectedJobs(selected);
                            setShowKickoffModal(false);
                        }}>
                        Kickoff {selected?.length} jobs
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Schedule */}
            <Modal size="xl" show={showEditSchedule}>
                <Modal.Header closeButton onClick={() => setShowEditSchedule(false)}>
                    <Modal.Title>Update Schedule {selectedPortalSchedule?.schedule_id}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {scheduleObj?.schedule_id === selectedPortalSchedule?.schedule_id &&
                    <div className={styles.SpiderSchedulingModalBody}>
                        <SpiderScheduleForm 
                            spiderName={selectedPortalSchedule?.spider_name} 
                            scheduleObj={scheduleObj} 
                            setScheduleObj={setScheduleObj}
                            source="SpiderScheduling"
                        />
                    </div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditSchedule(false)}>
                        Close
                    </Button>
                    <Button variant="success" onClick={() => 
                        {
                            // deleteSchedule(selectedPortalSchedule);
                            updateSchedule();
                            setShowEditSchedule(false);
                        }}>
                        Update Schedule
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Kickoff Schedule */}
            <Modal size="xl" show={showKickoffPortalSchedule}>
                <Modal.Header closeButton onClick={() => setShowKickoffPortalSchedule(false)}>
                    <Modal.Title>Are you sure you want to kick off {selectedPortalSchedule?.schedule_id}?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className={styles.SpiderSchedulingModalBody}>
                        <span><strong>Schedule ID:</strong> {selectedPortalSchedule?.schedule_id}</span>
                        <span><strong>Spider Key:</strong> {selectedPortalSchedule?.spider_key}</span>
                        <span><strong>Spider Name:</strong> {selectedPortalSchedule?.spider_name}</span>
                        <span><strong>Frequency:</strong> {selectedPortalSchedule?.frequency}</span>
                        <span><strong>Job Arguments:</strong></span>
                        {scheduleObj?.spiderArgs && 
                            <AGTable
                                data={scheduleObj?.spiderArgs}
                                keepRawHeaders={true}
                                gridRef={gridRef}
                                id={scheduleObj?.spiderArgs}
                            />
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowKickoffPortalSchedule(false)}>
                        Close
                    </Button>
                    <Button variant="success" onClick={() => 
                            {
                                kickoffSelectedJobs(JSON.parse(selectedPortalSchedule?.spider_args_raw).map((job) => ({
                                        spider_key: selectedPortalSchedule?.spider_key, 
                                        spider_name: selectedPortalSchedule?.spider_name,
                                        spider_args_raw: job,
                                        client: selectedPortalSchedule?.client,
                                        tag: selectedPortalSchedule?.tag
                                    }))
                                );                           
                                setShowKickoffPortalSchedule(false);
                        }}>
                        Kickoff
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Schedule */}
            <Modal size="xl" show={showDeleteSchedule}>
                <Modal.Header closeButton onClick={() => setShowDeleteSchedule(false)}>
                    <Modal.Title>Are you sure you want to delete this schedule</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className={styles.SpiderSchedulingModalBody}>
                        <span><strong>Schedule ID:</strong> {selectedPortalSchedule?.schedule_id}</span>
                        <span><strong>Spider Key:</strong> {selectedPortalSchedule?.spider_key}</span>
                        <span><strong>Spider Name:</strong> {selectedPortalSchedule?.spider_name}</span>
                        <span><strong>Frequency:</strong> {selectedPortalSchedule?.frequency}</span>
                        <span><strong>Job Arguments:</strong></span>
                        {scheduleObj?.spiderArgs && 
                            <AGTable
                                data={scheduleObj?.spiderArgs}
                                keepRawHeaders={true}
                                gridRef={gridRef}
                                id={scheduleObj?.spiderArgs}
                            />
                        }
                    </div>
      
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteSchedule(false)}>
                        Close
                    </Button>
                    <Button variant="danger" onClick={() => 
                        {
                            deleteSchedule(selectedPortalSchedule);
                            setShowDeleteSchedule(false);
                        }}>
                        Delete schedule
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className={props?.parent === "Dashboard" ? styles.SpiderSchedulingDashboard : styles.SpiderScheduling}>
                {location?.pathname === "/spider-scheduling" ? <h4>Spider Scheduling</h4> : <a href="/spider-library"><h4>Spider Scheduling</h4> </a>}
                {/* Options for Spider Scheduling Page */}
                <div className={styles.SpiderSchedulingButtonSpacing}>
                    <Button variant={source === "portal" ? "dark" : "outline-dark" } onClick={() => setSource("portal")}>Portal Schedules</Button>
                    <Button variant={source === "existing" ? "dark" : "outline-dark" } onClick={() => setSource("existing")}>Existing Schedules</Button>
                    <Button variant={source === "logs" ? "dark" : "outline-dark" } onClick={() => setSource("logs")}>Logs</Button>
                </div>
                {source === "existing" && <div>
                    <div style={{ display: "flex", gap: "20px"}}>
                        <div style={{flex: 8}}>
                            <Form.Control type="text" placeholder="Filter by Spider Name" value={spiderFilter} onChange={(e) => setSpiderFilter(e.target.value)}/>
                        </div>
                        {/* <div style={{flex: 4}}>
                            <Select/>
                            </div> */}
                        <div style={{flex: 1}}>
                            <Button className="w-100" variant="success" disabled={!selected || selected.length === 0} onClick={() => setShowKickoffModal(true)}>Kickoff Selected</Button>
                        </div>
                    </div>
                    <br/>
                    {(!loading && spiderLibraryData) && 
                        <AGTable data={flattenData(spiderLibraryData, ['vertical', 'project', 'client', 'spider_key', 'spider_name', 'spider_args', 'frequency', 'last_scrape_date'])} 
                        gridRef={gridRef}
                        columnsToHide={["spider_args_raw"]} 
                        fileName="spider-scheduling.csv"
                        // editableFields={["barcode_checked", "matching_checked"]}
                        showSelect={true}
                        onGridReady={onGridReady}
                        setSelected={setSelected}
                        onSelectionChanged={onSelectionChanged}
                        getRowId={getRowId}
                        />
                    }

                    {loading && <div className={styles.spinnerContainer}>
                        <Spinner/>
                    </div>}
                </div>}
                {source === "portal" && <div>
                    <div style={{ display: "flex", gap: "20px"}}>
                        <div style={{flex: 6}}>
                            <Form.Control type="text" placeholder="Filter by Spider Name" value={spiderFilter} onChange={(e) => setSpiderFilter(e.target.value)}/>
                        </div>
                        <div style={{flex: 6}}>
                            <Select isMulti placeholder="Filter by Client" 
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                                menuPortalTarget={document.body}  // Render dropdown at body level
                                options={[...new Set(portalScheduleData?.map(item => item.client))]
                                    .sort()
                                    .map(item => ({
                                    value: item,
                                    label: item
                                    }))}
                                value={selectedClients}   
                                onChange={setSelectedClients}                   
                                />
                            </div>
                    </div>
                    <br/>
                    {(!loading && portalScheduleData) && 
                        <AGTable data={flattenData(portalScheduleData, ['spider_key', 'spider_name', 'frequency']).map((obj) => {
                            return {
                                ...obj,
                                actions: <div>
                                    <IconButton disableRipple onClick={() => {
                                        setShowKickoffPortalSchedule(true);
                                        setSelectedPortalSchedule(portalScheduleData?.filter(schedule => schedule?.schedule_id === obj?.schedule_id)[0]);
                                    }}>
                                        <PlayCircleIcon/>
                                    </IconButton >
                                    <IconButton disableRipple onClick={() => {
                                        setSelectedPortalSchedule(portalScheduleData?.filter(schedule => schedule?.schedule_id === obj?.schedule_id)[0]);
                                        setShowEditSchedule(true);
                                    }}>
                                        <EditIcon/>
                                    </IconButton >
                                    <IconButton disableRipple onClick={() => {
                                        setShowDeleteSchedule(true);
                                        setSelectedPortalSchedule(portalScheduleData?.filter(schedule => schedule?.schedule_id === obj?.schedule_id)[0]);
                                    }}>
                                        <DeleteIcon/>
                                    </IconButton >
                                </div>
                            }
                        })} 
                        gridRef={gridRef}
                        columnsToHide={["day", "day_of_week", "spider_args_raw"]} 
                        fileName="spider-scheduling.csv"
                        showSelect={true}
                        onGridReady={onGridReady}
                        setSelected={setSelected}
                        onSelectionChanged={onSelectionChanged}
                        getRowId={getRowId}
                        />
                    }
                    {loading && <div className={styles.spinnerContainer}>
                        <Spinner/>
                    </div>}
                </div>}
                {source === "logs" && <div>
                    {(!loading && logs) && <AGTable
                        data={logs}
                        customFlex={[
                            {flex: 8, col: "message"}
                        ]}
                    />}
                    {loading && <div className={styles.spinnerContainer}>
                        <Spinner/>
                    </div>}
                </div>}
            </div>
        </div>
    )
};

export default SpiderScheduling;